import React, { useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";

import QuizQuestionModal from "./QuizQuestionModal";
import {
  QUIZ_MAX_QUESTIONS,
  QUIZ_MIN_QUESTIONS,
  QUIZ_PAGE_SIZES,
  downloadSampleQuizCsv,
  exportQuizCsv,
  parseQuizCsv,
  validateQuizRowForImport,
} from "./quizHelpers";

function truncate(text, max = 80) {
  const s = String(text ?? "");
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

const MIN_CATEGORIES_FOR_QUESTIONS = 3;

export default function QuizTab({
  questions,
  setQuestions,
  isSaving,
  onPersist,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
  newQuestionId,
  categories,
}) {
  const fileInputRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const persistList = async (nextList, message) => {
    setErrorMessage("");
    try {
      await onPersist(nextList);
      setQuestions(nextList);
      if (message) setSuccessMessage(message);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to save questions.");
      throw err;
    }
  };

  const saveMessage = (action, count) => {
    if (count < QUIZ_MIN_QUESTIONS) {
      const remaining = QUIZ_MIN_QUESTIONS - count;
      return `Saved to theme. Add ${remaining} more question${remaining === 1 ? "" : "s"} for the game (minimum ${QUIZ_MIN_QUESTIONS}).`;
    }
    if (action === "edit") return "Question updated and saved.";
    if (action === "delete") return "Question deleted.";
    return "Question added and saved.";
  };

  const openAdd = () => {
    if (questions.length >= QUIZ_MAX_QUESTIONS) {
      setErrorMessage(`You can add up to ${QUIZ_MAX_QUESTIONS} questions.`);
      return;
    }
    if ((categories || []).length < MIN_CATEGORIES_FOR_QUESTIONS) {
      setErrorMessage(
        `Add at least ${MIN_CATEGORIES_FOR_QUESTIONS} answer categories in Rules & Settings before adding questions.`
      );
      return;
    }
    setErrorMessage("");
    setModalMode("add");
    setEditingQuestion(null);
    setModalOpen(true);
  };

  const openEdit = (q) => {
    setErrorMessage("");
    setModalMode("edit");
    setEditingQuestion(q);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingQuestion(null);
  };

  const handleModalSubmit = async (row) => {
    const rowId = row.id || newQuestionId();
    const hasQuestionText = (q) => Boolean(String(q?.question_name ?? "").trim());

    let next;
    if (modalMode === "add") {
      // Drop legacy rows that only have options (no question text) so a valid
      // new question can be saved without editing every broken row first.
      const completeExisting = questions.filter(hasQuestionText);
      next = [...completeExisting, { id: rowId, ...row }];
    } else {
      next = questions.map((q) => (q.id === row.id ? { id: q.id, ...row } : q));
      const stillBroken = next.filter((q) => !hasQuestionText(q));
      if (stillBroken.length) {
        // Keep other incomplete rows out of this save so editing one row works.
        next = next.filter(hasQuestionText);
      }
    }

    if (!hasQuestionText(row)) {
      setErrorMessage("Question text is required.");
      return;
    }

    setErrorMessage("");
    try {
      await persistList(next, saveMessage(modalMode === "edit" ? "edit" : "add", next.length));
      closeModal();
    } catch {
      /* parent/persist surfaces error */
    }
  };

  const handleDelete = async (q) => {
    const isComplete = (row) => Boolean(String(row?.question_name ?? "").trim());
    const completeCount = questions.filter(isComplete).length;
    const next = questions.filter((row) => row.id !== q.id);
    const completeNext = next.filter(isComplete);

    // Once the minimum is met, do not allow deleting below it. While still
    // building the list (under the minimum), deletes stay allowed.
    if (completeCount >= QUIZ_MIN_QUESTIONS && completeNext.length < QUIZ_MIN_QUESTIONS) {
      await Swal.fire({
        icon: "warning",
        title: "Limit",
        text: `You need at least ${QUIZ_MIN_QUESTIONS} questions. Delete is not allowed when ${QUIZ_MIN_QUESTIONS} or fewer remain.`,
      });
      return;
    }

    const isIncomplete = !isComplete(q);
    const result = await Swal.fire({
      title: "Delete question?",
      text: isIncomplete
        ? "This incomplete question will be removed so you can add a full question."
        : "This question will be removed from the theme.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed) return;

    await persistList(completeNext, saveMessage("delete", completeNext.length));
  };

  const showCsvUploadError = (message, options = {}) => {
    const { code } = options;
    const isCharLimit = code === "CHAR_LIMIT" || /Maximum allowed is \d+ characters/i.test(message);
    const title = isCharLimit ? "Character limit exceeded" : "CSV upload failed";
    setSuccessMessage("");
    setErrorMessage(message);
    Swal.fire({
      icon: "error",
      title,
      text: message,
      confirmButtonText: "OK",
      confirmButtonColor: "#39b467",
    });
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!/\.csv$/i.test(file.name)) {
      showCsvUploadError("Please upload a .csv file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = parseQuizCsv(reader.result);

        if (parsed.length < QUIZ_MIN_QUESTIONS) {
          showCsvUploadError(
            `CSV has ${parsed.length} questions. At least ${QUIZ_MIN_QUESTIONS} are required.`
          );
          return;
        }

        if (parsed.length > QUIZ_MAX_QUESTIONS) {
          showCsvUploadError(
            `CSV has ${parsed.length} questions. Maximum allowed is ${QUIZ_MAX_QUESTIONS}.`
          );
          return;
        }

        const validated = [];
        for (let i = 0; i < parsed.length; i++) {
          const result = validateQuizRowForImport(parsed[i], `CSV row ${i + 2}`, categories);
          if (!result.ok) {
            showCsvUploadError(result.message, { code: result.code });
            return;
          }
          validated.push({ id: newQuestionId(), ...result.data });
        }

        setErrorMessage("");
        await persistList(
          validated,
          `Replaced all questions with ${validated.length} imported from CSV.`
        );
        setPage(1);
      } catch (err) {
        showCsvUploadError(err?.message || "Failed to parse CSV.");
      }
    };
    reader.onerror = () => showCsvUploadError("Could not read the CSV file.");
    reader.readAsText(file);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return questions;
    return questions.filter(
      (row) =>
        row.question_name?.toLowerCase().includes(q) ||
        row.correct_answer?.toLowerCase().includes(q) ||
        row.option_one?.toLowerCase().includes(q) ||
        row.option_two?.toLowerCase().includes(q) ||
        row.option_three?.toLowerCase().includes(q) ||
        row.option_four?.toLowerCase().includes(q) ||
        row.option_five?.toLowerCase().includes(q) ||
        row.option_six?.toLowerCase().includes(q)
    );
  }, [questions, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const completeCount = questions.filter((row) =>
    Boolean(String(row?.question_name ?? "").trim())
  ).length;

  return (
    <div className="themeupdate-panel-wide quiz-panel" data-tour="tour-gc-quiz">
      <div className="quiz-page-header">
        <h2 className="quiz-page-title">Add Questions</h2>
        <div className="quiz-toolbar">
          <button type="button" className="quiz-toolbar-btn" onClick={openAdd}>
            <i className="fa-solid fa-plus" aria-hidden /> Add Question
          </button>
          <label className="quiz-toolbar-btn quiz-upload-label">
            <i className="fa-solid fa-upload" aria-hidden /> Upload CSV
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="quiz-upload-input"
              onChange={handleCsvUpload}
              disabled={isSaving}
            />
          </label>
          <button
            type="button"
            className="quiz-toolbar-btn"
            onClick={() => exportQuizCsv(questions)}
            disabled={!questions.length}
          >
            <i className="fa-regular fa-file-lines" aria-hidden /> Export CSV
          </button>
          <button type="button" className="quiz-toolbar-btn" onClick={downloadSampleQuizCsv}>
            <i className="fa-solid fa-download" aria-hidden /> Sample CSV
          </button>
        </div>
      </div>

      <p className="qa-char-limit-hint">
        * Add questions (minimum {QUIZ_MIN_QUESTIONS}, maximum {QUIZ_MAX_QUESTIONS}). Every question
        uses the answer categories set on the Rules &amp; Settings page as its options — pick the
        correct one for each question. For CSV uploads, each row just needs question_name and
        correct_answer — correct_answer must exactly match one of your configured Answer Categories.
      </p>

      {(errorMessage || successMessage) && (
        <div className="qa-alerts">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      {questions.length > 0 && questions.length < QUIZ_MIN_QUESTIONS ? (
        <p className="cr-alert cr-alert-info quiz-min-hint">
          {questions.length} question{questions.length === 1 ? "" : "s"} saved. Add{" "}
          {QUIZ_MIN_QUESTIONS - questions.length} more for the game (minimum {QUIZ_MIN_QUESTIONS}).
        </p>
      ) : null}

      <div className="quiz-table-card themeupdate-card">
        <div className="quiz-table-controls">
          <label className="quiz-table-show">
            Show{" "}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {QUIZ_PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>{" "}
            entries
          </label>
          <label className="quiz-table-search">
            Search:{" "}
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder=""
            />
          </label>
        </div>

        <div className="quiz-table-wrap">
          <table className="quiz-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Question Name</th>
                <th>Correct Answer</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="quiz-table-empty">
                    No questions yet. Click <strong>Add Question</strong> or upload a CSV.
                  </td>
                </tr>
              ) : (
                pageRows.map((q, idx) => (
                  <tr key={q.id}>
                    <td>{(safePage - 1) * pageSize + idx + 1}</td>
                    <td className="quiz-cell-question" title={q.question_name}>
                      {q.question_name ? truncate(q.question_name, 40) : <em>No question</em>}
                    </td>
                    <td className="quiz-correct-badge" title={q.correct_answer}>
                      {truncate(q.correct_answer, 40) || "—"}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="quiz-table-btn quiz-table-btn-edit"
                        onClick={() => openEdit(q)}
                      >
                        <i className="fa-solid fa-pencil" aria-hidden /> Edit
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="quiz-table-btn quiz-table-btn-delete"
                        onClick={() => handleDelete(q)}
                        disabled={
                          isSaving ||
                          (completeCount >= QUIZ_MIN_QUESTIONS &&
                            Boolean(String(q?.question_name ?? "").trim()) &&
                            completeCount - 1 < QUIZ_MIN_QUESTIONS)
                        }
                        title={
                          completeCount >= QUIZ_MIN_QUESTIONS &&
                          Boolean(String(q?.question_name ?? "").trim()) &&
                          completeCount - 1 < QUIZ_MIN_QUESTIONS
                            ? `Minimum ${QUIZ_MIN_QUESTIONS} questions required`
                            : "Delete question"
                        }
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="quiz-table-footer">
          <span className="quiz-table-info">
            Showing {filtered.length ? (safePage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(safePage * pageSize, filtered.length)} of {filtered.length} entries
            {search ? ` (filtered from ${questions.length})` : ""}
          </span>
          <div className="quiz-table-pager">
            <button
              type="button"
              className="quiz-pager-btn"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="quiz-pager-num">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              className="quiz-pager-btn"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <QuizQuestionModal
        open={modalOpen}
        mode={modalMode}
        initial={editingQuestion}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isSaving={isSaving}
        categories={categories}
      />
    </div>
  );
}
